import React, { useState } from 'react';
import { MiniMonthCalendar } from './InteractiveCalendar';

export const ClinicOverviewPanel = ({ workspace, appointments = [], consultMode, onToggleConsultMode, onOpenReport, onOpenQuickConsult, onUploadTemplate, saving }) => {
  const isCommunity = workspace.user.role === 'community_manager';
  const [excelFile, setExcelFile] = useState(null);
  const importSummary = workspace.excel_template_status?.import_summary;
  const topLab = workspace.analytics?.top_laboratories?.[0];

  const submitTemplate = async (event) => {
    event.preventDefault();
    if (!excelFile) return;
    const success = await onUploadTemplate(excelFile);
    if (success) setExcelFile(null);
  };

  return (
  <section className="grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" data-testid="clinic-overview-panel">
    <article className="rounded-[28px] border border-white/60 bg-white/90 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Foco del día</p>
          <h2 className="mt-2 text-2xl font-black text-slate-900">{isCommunity ? 'Calendario editorial inmediato' : 'Lo que no puede esperar'}</h2>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500" data-testid="clinic-overview-updated-at">
          Actualizado {new Date(workspace.updated_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <div className="mt-5 space-y-3" data-testid="clinic-overview-focus-list">
        {workspace.today_focus?.length ? workspace.today_focus.map((item) => (
          <div key={item.id} className="rounded-[22px] border border-slate-100 bg-[#f8fafc] p-4" data-testid={`clinic-focus-${item.id}`}>
            <p className="text-sm font-semibold text-slate-900">{item.label}</p>
            <p className="mt-1 text-sm text-slate-600">{item.detail}</p>
          </div>
        )) : <p className="text-sm text-slate-500" data-testid="clinic-overview-empty">Todo está despejado ahora mismo.</p>}
      </div>
    </article>

    <div className="space-y-5">
      <article className="rounded-[28px] border border-white/60 bg-[#102a43] p-6 text-white shadow-[0_20px_60px_rgba(16,42,67,0.32)]" data-testid="clinic-overview-calendar-card">
        <p className="text-xs uppercase tracking-[0.24em] text-cyan-200">{isCommunity ? 'Operación editorial' : 'Calendario compartido'}</p>
        <div className="mt-4 space-y-3">
          {isCommunity ? (
            <>
              <p className="text-sm text-cyan-100" data-testid="clinic-overview-social-note">Las integraciones sociales LIVE quedan preparadas; en cuanto entren credenciales Meta/WhatsApp las conectamos sin tocar el flujo interno.</p>
              <div className="rounded-2xl bg-white/10 px-4 py-3 text-sm" data-testid="clinic-overview-social-pipeline">Pipeline editorial: idea → producción → aprobado → programado → publicado</div>
            </>
          ) : <MiniMonthCalendar appointments={appointments} />}
        </div>
      </article>

      {isCommunity ? (
        <article className="rounded-[28px] border border-slate-200 bg-white p-6" data-testid="clinic-overview-excel-card">
          <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Estado</p>
          <h3 className="mt-2 text-lg font-bold text-slate-900">Hub social listo para conectar</h3>
          <p className="mt-3 text-sm text-slate-600" data-testid="clinic-overview-excel-status">Puedes organizar campañas y aprobaciones hoy; la publicación real se conecta cuando entren credenciales finales.</p>
        </article>
      ) : (
        <article className="rounded-[28px] border border-slate-200 bg-white p-6" data-testid="clinic-overview-excel-card">
          <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Modo consulta + reportes</p>
          <h3 className="mt-2 text-lg font-bold text-slate-900">Tablet, móvil y escritorio</h3>
          <p className="mt-3 text-sm text-slate-600" data-testid="clinic-overview-excel-status">{workspace.excel_template_status?.message}</p>
          {!!workspace.excel_template_status?.sheet_preview?.length && (
            <div className="mt-4 rounded-[22px] bg-[#f8fafc] p-4" data-testid="clinic-overview-excel-preview">
              {workspace.excel_template_status.sheet_preview.map((sheet) => (
                <div key={sheet.sheet_name} className="mb-3 last:mb-0" data-testid={`clinic-overview-excel-sheet-${sheet.sheet_name}`}>
                  <p className="text-sm font-semibold text-slate-900">{sheet.sheet_name}</p>
                  <p className="mt-1 text-xs text-slate-500">Headers: {(sheet.headers || []).join(' · ') || 'sin cabeceras detectadas'}</p>
                </div>
              ))}
            </div>
          )}
          {!!importSummary && (
            <div className="mt-4 space-y-4" data-testid="clinic-overview-import-summary-card">
              <div className="grid gap-3 sm:grid-cols-2" data-testid="clinic-overview-import-summary-grid">
                <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid="clinic-overview-import-contacts">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Contactos limpios</p>
                  <p className="mt-2 text-2xl font-black text-slate-900">{importSummary.contacts_imported || 0}</p>
                  <p className="mt-1 text-xs text-slate-500">Con historial: {importSummary.contacts_with_history || 0}</p>
                </div>
                <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid="clinic-overview-import-histories">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Historiales</p>
                  <p className="mt-2 text-2xl font-black text-slate-900">{importSummary.histories_imported || 0}</p>
                  <p className="mt-1 text-xs text-slate-500">Inventario: {importSummary.inventory_items_imported || 0}</p>
                </div>
                <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid="clinic-overview-import-range">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Rango detectado</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{importSummary.date_range?.from || 'sin fecha'} → {importSummary.date_range?.to || 'sin fecha'}</p>
                </div>
                <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid="clinic-overview-import-duplicates">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Duplicados unificados</p>
                  <p className="mt-2 text-2xl font-black text-slate-900">{importSummary.duplicate_contacts_merged || 0}</p>
                  <p className="mt-1 text-xs text-slate-500">Sin historial: {importSummary.contacts_without_history || 0}</p>
                </div>
              </div>
              <div className="rounded-[22px] border border-slate-200 bg-white p-4" data-testid="clinic-overview-import-insights">
                <p className="text-sm font-semibold text-slate-900">Puntos en común detectados</p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="clinic-overview-import-top-treatments">
                  {(importSummary.top_treatments || []).slice(0, 4).map((item) => (
                    <span key={item.label} className="rounded-full bg-[#e0f2fe] px-3 py-1 text-xs font-semibold text-[#075985]">{item.label} · {item.count}</span>
                  ))}
                  {!(importSummary.top_treatments || []).length && <span className="text-xs text-slate-500">Aún no hay patrones destacados.</span>}
                </div>
                {!!importSummary.recommended_actions?.length && (
                  <ul className="mt-3 space-y-2 text-sm text-slate-600" data-testid="clinic-overview-import-recommendations">
                    {importSummary.recommended_actions.map((item) => <li key={item}>• {item}</li>)}
                  </ul>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2" data-testid="clinic-overview-lab-template-grid">
                <div className="rounded-[22px] border border-slate-200 bg-white p-4" data-testid="clinic-overview-top-lab-card">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Laboratorio / marca top</p>
                  <p className="mt-2 text-lg font-black text-slate-900">{topLab?.brand || 'Sin ranking todavía'}</p>
                  <p className="mt-1 text-xs text-slate-500">Stock {topLab?.stock_count || 0} · consumo {topLab?.usage_units || 0}</p>
                </div>
                <div className="rounded-[22px] border border-slate-200 bg-white p-4" data-testid="clinic-overview-master-template-card">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Plantilla clínica táctil</p>
                  <p className="mt-2 text-lg font-black text-slate-900">{workspace.clinical_master_template?.page_count || 0} páginas listas</p>
                  <p className="mt-1 text-xs text-slate-500">Historia general, seguimiento, plan de inyección y ficha de procedimiento.</p>
                </div>
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={onToggleConsultMode} className={`rounded-full px-4 py-2 text-sm font-semibold ${consultMode ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`} data-testid="clinic-overview-consult-mode-button">
              {consultMode ? 'Consulta táctil activada' : 'Activar consulta táctil'}
            </button>
            <button type="button" onClick={onOpenQuickConsult} className="rounded-full bg-[#102a43] px-4 py-2 text-sm font-semibold text-white" data-testid="clinic-overview-open-quick-consult-button">Abrir consulta rápida</button>
            <button type="button" onClick={() => onOpenReport(workspace.report_export_urls?.inventory)} className="rounded-full bg-[#e2e8f0] px-4 py-2 text-sm font-semibold text-slate-700" data-testid="clinic-overview-export-inventory-button">PDF inventario</button>
            <button type="button" onClick={() => onOpenReport(workspace.report_export_urls?.agenda)} className="rounded-full bg-[#dbeafe] px-4 py-2 text-sm font-semibold text-[#1d4ed8]" data-testid="clinic-overview-export-agenda-button">PDF agenda</button>
          </div>
          <form onSubmit={submitTemplate} className="mt-4 rounded-[22px] border border-dashed border-slate-300 p-4" data-testid="clinic-overview-template-upload-form">
            <p className="text-sm font-semibold text-slate-900">Subir Excel clínico provisional</p>
            <p className="mt-1 text-xs text-slate-500">Sube tu Excel aquí y te mostraré hojas/cabeceras detectadas dentro del panel.</p>
            <input data-testid="clinic-overview-template-file-input" type="file" accept=".xlsx,.xls,.csv" className="mt-3 w-full rounded-2xl border border-slate-200 px-4 py-3" onChange={(event) => setExcelFile(event.target.files?.[0] || null)} />
            <button data-testid="clinic-overview-template-upload-button" type="submit" className="mt-3 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white" disabled={!excelFile || saving === 'template-upload'}>
              {saving === 'template-upload' ? 'Subiendo…' : 'Subir Excel'}
            </button>
          </form>
        </article>
      )}
    </div>
  </section>
  );
};

